// src/components/TextCompare/InputSection.tsx

import React, { useRef, useState, ChangeEvent, DragEvent, ClipboardEvent } from 'react';
import { FileText, Copy, Upload, Trash2, Zap, Smartphone as FileUp, Check } from 'lucide-react';

interface GutterProps {
    text: string;
    gutterRef: any;
}

const Gutter: React.FC<GutterProps> = ({ text, gutterRef }) => {
    const lines = (text || '').split('\n');
    const lineCount = lines.length || 1;
    const digits = String(lineCount).length;
    const width = Math.max(40, digits * 8 + 20);

    return (
        <div
            className="absolute left-0 top-0 bottom-0 bg-[#f8fafc] border-r border-slate-200 text-slate-400 overflow-hidden py-4 z-[5] select-none"
            ref={gutterRef}
            style={{ width: `${width}px` }}
        >
            {lines.map((line, i) => (
                <div key={i} className="flex text-[14px] leading-[24px] min-h-[24px]">
                    <span className="w-full text-right pr-2 shrink-0">{i + 1}</span>
                    <span className="absolute invisible pointer-events-none whitespace-pre-wrap break-all" aria-hidden="true">
                        {line || ' '}
                    </span>
                </div>
            ))}
        </div>
    );
};

interface InputSectionProps {
    oldText: string;
    newText: string;
    setOldText: (text: string) => void;
    setNewText: (text: string) => void;
    fileName1: string;
    fileName2: string;
    setFileName1: (name: string) => void;
    setFileName2: (name: string) => void;
    onCompare: () => void;
    onFileUpload: (file: File, side: 1 | 2) => void;
    isLoading: boolean;
}

const InputSection: React.FC<InputSectionProps> = ({
    oldText,
    newText,
    setOldText,
    setNewText,
    fileName1,
    fileName2,
    setFileName1,
    setFileName2,
    onCompare,
    onFileUpload,
    isLoading
}) => {
    const file1Ref = useRef<HTMLInputElement>(null);
    const file2Ref = useRef<HTMLInputElement>(null);
    const textarea1Ref = useRef<HTMLTextAreaElement>(null);
    const textarea2Ref = useRef<HTMLTextAreaElement>(null);
    const gutter1Ref = useRef<HTMLDivElement>(null);
    const gutter2Ref = useRef<HTMLDivElement>(null);

    const [isDragOver1, setIsDragOver1] = useState(false);
    const [isDragOver2, setIsDragOver2] = useState(false);
    const [copied1, setCopied1] = useState(false);
    const [copied2, setCopied2] = useState(false);
    const [suppressingHover, setSuppressingHover] = useState<number | null>(null); // null, 1, or 2

    const triggerHoverSuppression = (side: number) => {
        setSuppressingHover(side);
        setTimeout(() => setSuppressingHover(null), 1000);
    };

    const handleFileUpload = (e: ChangeEvent<HTMLInputElement>, side: 1 | 2) => {
        const file = e.target.files?.[0];
        if (file) {
            onFileUpload(file, side);
            e.target.value = '';
            triggerHoverSuppression(side);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>, setDragState: (s: boolean) => void) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>, setDragState: (s: boolean) => void) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>, setDragState: (s: boolean) => void, side: 1 | 2) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            onFileUpload(files[0], side);
        }
    };

    const handlePaste = async (e: ClipboardEvent<HTMLTextAreaElement>, side: 1 | 2) => {
        const items = e.clipboardData.items;
        let fileFound = false;

        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                const file = items[i].getAsFile();
                if (file) {
                    onFileUpload(file, side);
                    fileFound = true;
                    break;
                }
            }
        }

        if (fileFound) {
            e.preventDefault();
        }
    };

    const handleClearSide = (setText: (t: string) => void, setFileName: (n: string) => void) => {
        setText('');
        setFileName('');
    };

    const handleCopy = (text: string, side: 1 | 2) => {
        navigator.clipboard.writeText(text);
        if (side === 1) {
            setCopied1(true);
            setTimeout(() => setCopied1(false), 2000);
        } else {
            setCopied2(true);
            setTimeout(() => setCopied2(false), 2000);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>, gutterRef: any) => {
        if (gutterRef.current) {
            gutterRef.current.scrollTop = e.currentTarget.scrollTop;
        }
    };

    const getTextareaPadding = (text: string) => {
        if (!text) return '1rem';
        const lineCount = text.split('\n').length || 1;
        const digits = String(lineCount).length;
        const gutterWidth = Math.max(40, digits * 8 + 20);
        return `1rem 1rem 1rem ${gutterWidth + 12}px`;
    };

    return (
        <div className="flex flex-col gap-6 w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-h-[500px]">
                {/* Left Panel: Original Content */}
                <div className="flex flex-col bg-white border border-[#eef2f6] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:border-slate-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] focus-within:!border-blue-500 focus-within:shadow-[0_0_0_4px_rgba(59,130,246,0.08),0_8px_30px_rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-center px-4 py-3 bg-transparent">
                        <div className="flex items-center gap-2 text-slate-600">
                            <FileText size={16} />
                            <span className="font-semibold text-[0.85rem] text-slate-800">{fileName1 || 'Original Content'}</span>
                        </div>
                        <div className="flex items-center gap-2 relative">
                            <input
                                type="file"
                                ref={file1Ref}
                                className="hidden"
                                onChange={(e) => handleFileUpload(e, 1)}
                            />
                            {oldText && (
                                <>
                                    <button
                                        key="copy-1"
                                        className={`flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-slate-500 cursor-pointer transition-all duration-200 hover:bg-slate-100 hover:text-blue-500 ${suppressingHover === 1 ? 'pointer-events-none' : ''}`}
                                        onClick={(e) => {
                                            handleCopy(oldText, 1);
                                            e.currentTarget.blur();
                                            triggerHoverSuppression(1);
                                        }}
                                        title={copied1 ? "Copied!" : "Copy"}
                                    >
                                        {copied1 ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                    </button>
                                    <button
                                        key="clear-1"
                                        className={`flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-slate-500 cursor-pointer transition-all duration-200 hover:bg-red-50 hover:text-red-500 ${suppressingHover === 1 ? 'pointer-events-none' : ''}`}
                                        onClick={(e) => {
                                            handleClearSide(setOldText, setFileName1);
                                            e.currentTarget.blur();
                                            triggerHoverSuppression(1);
                                        }}
                                        title="Clear"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </>
                            )}
                            <button
                                key="upload-1"
                                className={`flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-slate-500 cursor-pointer transition-all duration-200 hover:bg-slate-100 hover:text-blue-500 ${suppressingHover === 1 ? 'pointer-events-none' : ''}`}
                                onClick={(e) => {
                                    file1Ref.current?.click();
                                    e.currentTarget.blur();
                                    triggerHoverSuppression(1);
                                }}
                                title="Upload File"
                            >
                                <Upload size={18} />
                            </button>
                        </div>
                    </div>
                    <div
                        className={`flex-1 relative flex ${oldText ? 'bg-slate-50' : 'bg-white'} ${isDragOver1 ? 'after:content-[\'\'] after:absolute after:inset-0 after:border-2 after:border-dashed after:border-blue-500 after:bg-blue-500/5 after:z-10 after:pointer-events-none' : ''}`}
                        onDragOver={(e) => handleDragOver(e, setIsDragOver1)}
                        onDragLeave={(e) => handleDragLeave(e, setIsDragOver1)}
                        onDrop={(e) => handleDrop(e, setIsDragOver1, 1)}
                    >
                        {oldText && <Gutter text={oldText} gutterRef={gutter1Ref} />}
                        <textarea
                            className="flex-1 w-full min-h-full resize-none border-none focus:outline-none font-['Inter',_'Segoe_UI',_sans-serif] text-[14px] leading-[24px] text-slate-800 bg-transparent m-0"
                            style={{ padding: getTextareaPadding(oldText) }}
                            ref={textarea1Ref}
                            value={oldText}
                            onChange={(e) => setOldText(e.target.value)}
                            onScroll={(e) => handleScroll(e, gutter1Ref)}
                            onPaste={(e) => handlePaste(e, 1)}
                            spellCheck="false"
                            aria-label="Original text input"
                        />
                        {isDragOver1 && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-[2px] z-20 pointer-events-none">
                                <div className="flex flex-col items-center gap-4 text-blue-500 bg-white py-6 px-12 rounded-2xl shadow-[0_10px_25px_-5px_rgba(59,130,246,0.2)] scale-110 animate-[slideUp_0.3s_ease-out]">
                                    <FileUp size={48} className="animate-[bounce_2s_infinite]" />
                                    <p className="m-0 font-semibold text-[1.1rem]">Drop to load file</p>
                                </div>
                            </div>
                        )}
                        {!oldText && !isDragOver1 && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-slate-400 pointer-events-none select-none">
                                <FileText size={36} strokeWidth={1.5} className="opacity-50" />
                                <p className="m-0 font-medium text-[0.95rem]">Paste text or drop a file to begin</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel: Changed Content */}
                <div className="flex flex-col bg-white border border-[#eef2f6] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:border-slate-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] focus-within:!border-blue-500 focus-within:shadow-[0_0_0_4px_rgba(59,130,246,0.08),0_8px_30px_rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-center px-4 py-3 bg-transparent">
                        <div className="flex items-center gap-2 text-slate-600">
                            <FileText size={16} />
                            <span className="font-semibold text-[0.85rem] text-slate-800">{fileName2 || 'Changed Content'}</span>
                        </div>
                        <div className="flex items-center gap-2 relative">
                            <input
                                type="file"
                                ref={file2Ref}
                                className="hidden"
                                onChange={(e) => handleFileUpload(e, 2)}
                            />
                            {newText && (
                                <>
                                    <button
                                        key="copy-2"
                                        className={`flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-slate-500 cursor-pointer transition-all duration-200 hover:bg-slate-100 hover:text-blue-500 ${suppressingHover === 2 ? 'pointer-events-none' : ''}`}
                                        onClick={(e) => {
                                            handleCopy(newText, 2);
                                            e.currentTarget.blur();
                                            triggerHoverSuppression(2);
                                        }}
                                        title={copied2 ? "Copied!" : "Copy"}
                                    >
                                        {copied2 ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                    </button>
                                    <button
                                        key="clear-2"
                                        className={`flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-slate-500 cursor-pointer transition-all duration-200 hover:bg-red-50 hover:text-red-500 ${suppressingHover === 2 ? 'pointer-events-none' : ''}`}
                                        onClick={(e) => {
                                            handleClearSide(setNewText, setFileName2);
                                            e.currentTarget.blur();
                                            triggerHoverSuppression(2);
                                        }}
                                        title="Clear"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </>
                            )}
                            <button
                                key="upload-2"
                                className={`flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-slate-500 cursor-pointer transition-all duration-200 hover:bg-slate-100 hover:text-blue-500 ${suppressingHover === 2 ? 'pointer-events-none' : ''}`}
                                onClick={(e) => {
                                    file2Ref.current?.click();
                                    e.currentTarget.blur();
                                    triggerHoverSuppression(2);
                                }}
                                title="Upload File"
                            >
                                <Upload size={18} />
                            </button>
                        </div>
                    </div>
                    <div
                        className={`flex-1 relative flex ${newText ? 'bg-slate-50' : 'bg-white'} ${isDragOver2 ? 'after:content-[\'\'] after:absolute after:inset-0 after:border-2 after:border-dashed after:border-blue-500 after:bg-blue-500/5 after:z-10 after:pointer-events-none' : ''}`}
                        onDragOver={(e) => handleDragOver(e, setIsDragOver2)}
                        onDragLeave={(e) => handleDragLeave(e, setIsDragOver2)}
                        onDrop={(e) => handleDrop(e, setIsDragOver2, 2)}
                    >
                        {newText && <Gutter text={newText} gutterRef={gutter2Ref} />}
                        <textarea
                            className="flex-1 w-full min-h-full resize-none border-none focus:outline-none font-['Inter',_'Segoe_UI',_sans-serif] text-[14px] leading-[24px] text-slate-800 bg-transparent m-0"
                            style={{ padding: getTextareaPadding(newText) }}
                            ref={textarea2Ref}
                            value={newText}
                            onChange={(e) => setNewText(e.target.value)}
                            onScroll={(e) => handleScroll(e, gutter2Ref)}
                            onPaste={(e) => handlePaste(e, 2)}
                            spellCheck="false"
                            aria-label="Changed text input"
                        />
                        {isDragOver2 && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-[2px] z-20 pointer-events-none">
                                <div className="flex flex-col items-center gap-4 text-blue-500 bg-white py-6 px-12 rounded-2xl shadow-[0_10px_25px_-5px_rgba(59,130,246,0.2)] scale-110 animate-[slideUp_0.3s_ease-out]">
                                    <FileUp size={48} className="animate-[bounce_2s_infinite]" />
                                    <p className="m-0 font-semibold text-[1.1rem]">Drop to load file</p>
                                </div>
                            </div>
                        )}
                        {!newText && !isDragOver2 && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-slate-400 pointer-events-none select-none">
                                <FileText size={36} strokeWidth={1.5} className="opacity-50" />
                                <p className="m-0 font-medium text-[0.95rem]">Paste text or drop a file to begin</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex justify-center mt-8">
                <button
                    className={`flex items-center justify-center gap-3 px-[3.5rem] py-[0.8rem] bg-[#1a73e8] text-white border-none rounded-full w-[320px] text-[1.05rem] font-medium cursor-pointer transition-all duration-200 shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)] hover:not(:disabled):bg-[#185abc] hover:not(:disabled):shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)] disabled:bg-[#f1f3f4] disabled:text-[#3c4043] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none ${isLoading ? 'relative text-transparent pointer-events-none !bg-[#1a73e8] !opacity-90 cursor-wait' : ''}`}
                    onClick={onCompare}
                    disabled={isLoading || (!oldText && !newText)}
                >
                    {isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center gap-[4px]">
                            <div className="w-2h h-2 rounded-full bg-white opacity-80 animate-[bouncing-loader_0.6s_infinite_alternate]"></div>
                            <div className="w-2 h-2 rounded-full bg-white opacity-80 animate-[bouncing-loader_0.6s_infinite_alternate] [animation-delay:0.2s]"></div>
                            <div className="w-2 h-2 rounded-full bg-white opacity-80 animate-[bouncing-loader_0.6s_infinite_alternate] [animation-delay:0.4s]"></div>
                        </div>
                    ) : (
                        <>
                            <Zap size={20} />
                            <span>Find Differences</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default InputSection;
